import type { EncounterSnapshot } from "@ayla/conversation";

export type PaceTokenCounts = {
  input_tokens: number;
  output_tokens: number;
  cached_input_tokens: number;
  uncached_input_tokens: number;
  total_tokens: number;
};

export type PaceConfidenceScore = {
  field_name: string;
  agreement_score: number;
};

export type PaceAuditMetadata = {
  timestamp: string;
  analyzer_version: string;
  analysis_id: string;
  schema_version?: number | null;
  instructions_version?: number | null;
  model_provider?: string | null;
  model_name?: string | null;
  parameter_hash?: string | null;
  latency_ms?: number | null;
  token_counts?: PaceTokenCounts | null;
  user_id?: string | null;
  sso_tenant_id?: string | null;
  site?: string | null;
  num_runs: number;
};

export type PaceAnalysisRecord = {
  data: Record<string, unknown>;
  audit_metadata: PaceAuditMetadata;
  confidence_scores: PaceConfidenceScore[];
  consistency_results?: Record<string, Record<string, unknown>> | null;
};

export type PaceHistoryResponse = {
  items: PaceAnalysisRecord[];
  limit: number;
  offset: number;
};

const toNumber = (value: unknown): number | null => {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim()) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

const toString = (value: unknown): string | null => {
  if (typeof value !== "string") return null;
  return value.trim() ? value.trim() : null;
};

const toBoolean = (value: unknown): boolean | null => {
  if (typeof value === "boolean") return value;
  if (typeof value === "string") {
    if (value.toLowerCase() === "true") return true;
    if (value.toLowerCase() === "false") return false;
  }
  return null;
};

export const getPaceNumber = (
  data: Record<string, unknown>,
  key: string
): number | null => toNumber(data[key]);

export const getPaceString = (
  data: Record<string, unknown>,
  key: string
): string | null => toString(data[key]);

export const getPaceBoolean = (
  data: Record<string, unknown>,
  key: string
): boolean | null => toBoolean(data[key]);

export const getPaceAnalysisId = (raw: unknown): string | null => {
  if (!raw || typeof raw !== "object") return null;
  const auditMetadata = (raw as { audit_metadata?: { analysis_id?: unknown } })
    .audit_metadata;
  const analysisId = auditMetadata?.analysis_id;
  return typeof analysisId === "string" && analysisId.trim()
    ? analysisId.trim()
    : null;
};

export const formatPaceTimestamp = (timestamp: string): string =>
  new Date(timestamp).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });

export const snapshotToPaceAnalysisRecord = (
  snapshot: EncounterSnapshot
): PaceAnalysisRecord => {
  const toRating = (value: number): number =>
    Math.max(1, Math.min(5, Math.round(value)));

  const agentRating = toRating((snapshot.talkClinicianPct || 0) / 20);
  const clarityOfExplanation = toRating((snapshot.openPct || 0) / 20);
  const empathyShown = toRating((snapshot.empathyCount ?? 0) + 1);

  return {
    data: {
      enrollment_success: snapshot.openPct >= 50,
      agent_rating: agentRating,
      clarity_of_explanation: clarityOfExplanation,
      empathy_shown: empathyShown,
      objection_handling: Math.max(
        1,
        Math.min(5, 5 - (snapshot.interruptionsCount ?? 0))
      ),
      call_duration_minutes: snapshot.duration ?? 0,
      key_strengths: "",
      areas_for_improvement: "",
    },
    audit_metadata: {
      analysis_id: snapshot.id,
      timestamp: new Date(snapshot.ts).toISOString(),
      analyzer_version: "demo",
      num_runs: 1,
      site: null,
    },
    confidence_scores: [],
    consistency_results: null,
  };
};
